import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Camera, X, AlertCircle, Smartphone } from 'lucide-react';
import jsQR from 'jsqr';
import Quagga from '@ericblade/quagga2';

interface QRCodeScannerProps {
  onScan: (data: string) => void;
  onError: (error: string) => void;
  onClose: () => void;
  isActive: boolean;
  scanType?: 'qr' | 'barcode' | 'both'; // Type of code to scan
  showInstructions?: boolean; // Show instruction texts (default: true)
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onScan,
  onError,
  onClose,
  isActive,
  scanType = 'qr',
  showInstructions = true
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [processingQR, setProcessingQR] = useState(false);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const scanningRef = useRef(false);
  const processingRef = useRef(false);
  const quaggaInitialized = useRef(false);
  const quaggaContainerRef = useRef<HTMLDivElement>(null);

  // Real QR code detection using jsQR library
  const detectQRCode = (imageData: ImageData): string | null => {
    try {
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });

      if (code) {
        return code.data;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  // Barcode detection using Quagga2 (works on all platforms including iOS)
  const detectBarcodeWithQuagga = async (canvas: HTMLCanvasElement): Promise<string | null> => {
    return new Promise((resolve) => {
      try {
        Quagga.decodeSingle({
          src: canvas.toDataURL(),
          numOfWorkers: 0,
          locate: true,
          decoder: {
            readers: ['ean_reader', 'ean_8_reader', 'code_128_reader', 'code_39_reader', 'upc_reader', 'upc_e_reader']
          }
        }, (result) => {
          if (result && result.codeResult) {
            resolve(result.codeResult.code || null);
          } else {
            resolve(null);
          }
        });
      } catch (error) {
        console.error('Quagga decode error:', error);
        resolve(null);
      }
    });
  };

  // Fallback to native BarcodeDetector if available (faster on Android)
  const detectBarcode = async (imageData: ImageData, canvas: HTMLCanvasElement): Promise<string | null> => {
    try {
      // Try native API first (faster on Android Chrome)
      if ('BarcodeDetector' in window) {
        const barcodeDetector = new (window as any).BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e']
        });

        const barcodes = await barcodeDetector.detect(canvas);
        if (barcodes.length > 0) {
          return barcodes[0].rawValue;
        }
      }

      // Fallback to Quagga2 for iOS and other browsers
      return await detectBarcodeWithQuagga(canvas);
    } catch (error) {
      // If native fails, try Quagga2
      return await detectBarcodeWithQuagga(canvas);
    }
  };

  const scanForQRCode = useCallback(async (video: HTMLVideoElement, canvas: HTMLCanvasElement, context: CanvasRenderingContext2D) => {
    // Don't scan if we're not in scanning mode or already processing
    if (!scanningRef.current || processingRef.current) {
      return;
    }

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get image data
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

    let detectedData: string | null = null;

    // Detect based on scanType
    if (scanType === 'qr' || scanType === 'both') {
      detectedData = detectQRCode(imageData);
    }

    // If no QR code found and we're looking for barcodes
    if (!detectedData && (scanType === 'barcode' || scanType === 'both')) {
      detectedData = await detectBarcode(imageData, canvas);
    }

    if (detectedData) {
      processingRef.current = true; // Prevent further processing
      scanningRef.current = false; // Stop scanning immediately

      // Clear the scan interval immediately
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }

      setProcessingQR(true);
      setScanning(false);
      onScan(detectedData); // Process the detected code

      // Stop camera manually here
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      setScanning(false);
      setProcessingQR(false);
      scanningRef.current = false;
      processingRef.current = false;
    }
  }, [onScan, stream, scanType]);

  const startScanning = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d', { willReadFrequently: true });

    if (!context) {
      return;
    }

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Start scanning interval - more frequent on mobile for better scanning
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const scanInterval = isMobile ? 300 : 500; // Faster scanning on mobile
    
    scanIntervalRef.current = setInterval(() => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        scanForQRCode(video, canvas, context);
      }
    }, scanInterval);
  }, [scanForQRCode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }

    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    // Stop Quagga if it was initialized
    if (quaggaInitialized.current) {
      try {
        Quagga.stop();
        quaggaInitialized.current = false;
      } catch (error) {
        console.error('Error stopping Quagga:', error);
      }
    }

    setScanning(false);
    setProcessingQR(false); // Reset processing state
    scanningRef.current = false;
    processingRef.current = false;
  }, [stream]);

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setProcessingQR(false); // Reset processing state
      processingRef.current = false;
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported in this browser');
      }

      // Try mobile-optimized camera access first
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      const constraints = {
        video: {
          facingMode: isMobile ? { ideal: 'environment' } : 'user', // Use back camera on mobile
          width: { ideal: isMobile ? 1280 : 640, min: 320 },
          height: { ideal: isMobile ? 720 : 480, min: 240 },
          frameRate: { ideal: 30, min: 15 }
        }
      };
      
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      if (videoRef.current) {
        const video = videoRef.current;
        video.srcObject = mediaStream;
        
        // Handle video loading
        video.onloadedmetadata = () => {
          setScanning(true);
          scanningRef.current = true;
          startScanning();
        };
        
        // Handle when video can play
        video.oncanplay = () => {};
        
        // Handle video errors
        video.onerror = (e) => {
          setError('Video playback error');
          onError('Video playback error');
        };
        
        // Handle video loaded data
        video.onloadeddata = () => {};
        
        try {
          await video.play();
        } catch (playError) {
          // Don't throw here, sometimes autoplay fails but video still works
          setTimeout(() => {
            video.play().catch(() => {});
          }, 100);
        }
      }
    } catch (err: any) {
      let errorMessage = 'Failed to access camera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permissions and refresh the page.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found. Please ensure your device has a camera.';
      } else if (err.name === 'NotSupportedError') {
        errorMessage = 'Camera not supported in this browser.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage = 'Camera constraints not supported.';
      } else {
        errorMessage = `Camera error: ${err.message}`;
      }
      
      setError(errorMessage);
      onError(errorMessage);
    }
  }, []); // Remove dependencies that cause infinite loops

  useEffect(() => {
    if (isActive) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isActive]); // Only depend on isActive to prevent infinite loops

  const handleTestScan = () => {
    // For testing purposes, simulate a QR code scan
    // Randomly choose between loyalty program and coupon offer
    const isLoyalty = Math.random() > 0.5;
    
    const testQRData = isLoyalty ? {
      customer_id: "CUST123",
      loyalty_program_id: "550e8400-e29b-41d4-a716-446655440000",
      business_id: "550e8400-e29b-41d4-a716-446655440001",
      pass_id: "550e8400-e29b-41d4-a716-446655440002",
      auth_token: "secure-token-hash",
      expires_at: new Date(Date.now() + 3600000).toISOString() // 1 hour from now
    } : {
      customer_id: "CUST456",
      coupon_offer_id: "550e8400-e29b-41d4-a716-446655440003",
      business_id: "550e8400-e29b-41d4-a716-446655440001",
      pass_id: "550e8400-e29b-41d4-a716-446655440004",
      auth_token: "secure-coupon-token",
      expires_at: new Date(Date.now() + 86400000).toISOString() // 24 hours from now
    };
    
    onScan(JSON.stringify(testQRData));
    
    // Stop camera manually here
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setScanning(false);
    setProcessingQR(false);
    scanningRef.current = false;
    processingRef.current = false;
  };

  if (!isActive) {
    return null;
  }

  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  return (
    <Card className={`w-full ${isMobile ? 'max-w-full' : 'max-w-4xl'} mx-auto`}>
      <CardContent className="p-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              {isMobile ? <Smartphone className="h-5 w-5" /> : <Camera className="h-5 w-5" />}
              {scanType === 'barcode' ? 'Barcode Scanner' : scanType === 'both' ? 'Code Scanner' : 'QR Code Scanner'} {isMobile ? '(Mobile)' : ''}
            </h3>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="relative">
            <video
              ref={videoRef}
              className={`w-full ${isMobile ? 'h-[32rem]' : 'h-[28rem]'} bg-black rounded-lg object-cover`}
              autoPlay
              playsInline
              muted
              controls={false}
              style={{ 
                transform: isMobile ? 'none' : 'scaleX(-1)', // Don't mirror on mobile for better scanning
                maxWidth: '100%',
                maxHeight: isMobile ? '80vh' : '28rem'
              }}
            />
            
            {scanning && showInstructions && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-64 border-2 border-blue-500 border-dashed rounded-lg flex items-center justify-center">
                  <div className="text-blue-500 text-sm text-center bg-white bg-opacity-80 p-2 rounded">
                    <div className="animate-pulse">
                      {scanType === 'barcode' ? 'Position barcode here' : scanType === 'both' ? 'Position code here' : 'Position QR code here'}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {!scanning && !error && stream && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-sm">Starting camera...</p>
                </div>
              </div>
            )}
            
            {!stream && !error && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800 rounded-lg">
                <div className="text-white text-center">
                  <Camera className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No camera feed</p>
                </div>
              </div>
            )}
          </div>

          <canvas
            ref={canvasRef}
            className="hidden"
          />

          {showInstructions && (
            <>
              {/* Auto-scan notice */}
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                <p className="text-sm text-blue-700">
                  {scanType === 'barcode'
                    ? 'Barcodes will be automatically detected and processed when visible'
                    : scanType === 'both'
                    ? 'QR codes and barcodes will be automatically detected when visible'
                    : 'QR codes will be automatically detected and processed when visible'
                  }
                </p>
              </div>

              <div className="text-xs text-gray-500 text-center">
                <p>
                  {isMobile
                    ? `Hold your phone steady and point the camera at the ${scanType === 'barcode' ? 'barcode' : scanType === 'both' ? 'code' : 'QR code'}`
                    : `Point your camera at the ${scanType === 'barcode' ? 'barcode' : scanType === 'both' ? 'code' : 'QR code'} on the customer's loyalty card`
                  }
                </p>
                <p className="mt-1">
                  <strong>Tip:</strong> {isMobile
                    ? `Use good lighting and hold the phone about ${scanType === 'barcode' ? '4-8' : '6-12'} inches from the ${scanType === 'barcode' ? 'barcode' : 'code'}`
                    : "Ensure good lighting for best results"
                  }
                </p>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QRCodeScanner;